import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

export interface ContainerProps {
  /**
   * The content to be wrapped by the container
   */
  children: ReactNode;

  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;

  /**
   * The HTML element to render as the container
   * @default "section"
   */
  as?: "div" | "section" | "article";
}

/**
 * Container component provides consistent spacing and layout constraints
 * for page-level content blocks.
 *
 * Applies responsive padding and margins:
 * - Mobile: 16px (p-4, my-4)
 * - Desktop (md+): 24px (md:p-6, md:my-6)
 *
 * @example
 * ```tsx
 * <Container as="section">
 *   <HeroBanner {...props} />
 * </Container>
 * ```
 */
export function Container(props: Readonly<ContainerProps>): ReactNode {
  const Component = props.as || "section";

  return (
    <Component className={cn("container h-full p-4 md:p-6", props.className)}>
      {props.children}
    </Component>
  );
}
